/** TODO の CRUD・トグル操作を提供するフック群 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "../lib/api";
import type { Todo } from "../types/todo";
import { SESSIONS_KEY } from "./use-shopping-session";

export const TODOS_KEY = ["todos"] as const;

export const todosQueryKey = (labelId?: string) =>
  [...TODOS_KEY, { labelId }] as const;

export const useTodos = (labelId?: string) => {
  return useQuery({
    queryKey: todosQueryKey(labelId),
    queryFn: async () => {
      const res = await api.index.$get({
        query: labelId ? { labelId } : {},
      });
      if (!res.ok) throw new Error("Failed to fetch todos");
      const json = await res.json();
      return json.data;
    },
  });
};

export const useCreateTodo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      title,
      labelIds,
    }: { title: string; labelIds?: string[] }) => {
      const res = await api.index.$post({ json: { title, labelIds } });
      if (!res.ok) throw new Error("Failed to create todo");
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TODOS_KEY });
    },
    onError: () => {
      toast.error("追加に失敗しました");
    },
  });
};

export const useTodo = (id: string | null) => {
  return useQuery({
    queryKey: [...TODOS_KEY, id],
    queryFn: async () => {
      const res = await api[":id"].$get({ param: { id: id as string } });
      if (!res.ok) throw new Error("Failed to fetch todo");
      const json = await res.json();
      return json.data;
    },
    enabled: id !== null,
  });
};

type UpdateTodoInput = {
  id: string;
  data: Record<string, unknown>;
};

export const useUpdateTodo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: UpdateTodoInput) => {
      const res = await api[":id"].$patch({ param: { id }, json: data });
      if (!res.ok) throw new Error("Failed to update todo");
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TODOS_KEY });
    },
    onError: (err) => {
      toast.error("更新に失敗しました");
    },
  });
};

export const useDeleteTodo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api[":id"].$delete({ param: { id } });
      if (!res.ok) throw new Error("Failed to delete todo");
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: TODOS_KEY });
      const snapshots = queryClient.getQueriesData<Todo[]>({
        queryKey: TODOS_KEY,
      });
      for (const [key, data] of snapshots) {
        if (!Array.isArray(data)) continue;
        queryClient.setQueryData<Todo[]>(key, (old) =>
          old?.filter((todo) => todo.id !== id),
        );
      }
      return { snapshots };
    },
    onError: (err, _id, context) => {
      for (const [key, data] of context?.snapshots ?? []) {
        queryClient.setQueryData(key, data);
      }
      toast.error("削除に失敗しました");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TODOS_KEY });
    },
  });
};

export const useToggleTodo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, checked }: { id: string; checked: boolean }) => {
      const res = await api[":id"].$patch({
        param: { id },
        json: { checked: !checked },
      });
      if (!res.ok) throw new Error("Failed to update todo");
      const json = await res.json();
      return json.data;
    },
    onMutate: async ({ id, checked }) => {
      await queryClient.cancelQueries({ queryKey: TODOS_KEY });
      const snapshots = queryClient.getQueriesData<Todo[]>({
        queryKey: TODOS_KEY,
      });
      for (const [key, data] of snapshots) {
        if (!Array.isArray(data)) continue;
        queryClient.setQueryData<Todo[]>(key, (old) =>
          old?.map((todo) =>
            todo.id === id ? { ...todo, checked: !checked } : todo,
          ),
        );
      }
      return { snapshots };
    },
    onError: (err, _vars, context) => {
      for (const [key, data] of context?.snapshots ?? []) {
        queryClient.setQueryData(key, data);
      }
      toast.error("更新に失敗しました");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TODOS_KEY });
      queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
    },
  });
};
